import {
  OnOpenChangeData,
  OpenPopoverEvents,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  PositioningShorthand,
} from '@fluentui/react-components';
import cn from 'classnames';
import React, { HTMLAttributes, ReactElement, ReactNode } from 'react';
import { useFluentMountNode } from '../hooks';
import './Popup.scss';

/** Position of the popup relative to the trigger */
export type PopupPosition = 'above' | 'below' | 'before' | 'after';

/** Alignment of the popup relative to the trigger */
export type PopupAlign = 'start' | 'center' | 'end';

/** Size variants for the popup */
export type PopupSize = 'small' | 'medium' | 'large';

export interface PopupProps extends Omit<HTMLAttributes<HTMLDivElement>, 'content'> {
  /** Element that triggers the popup when clicked */
  trigger: ReactElement;
  /** Content to display inside the popup */
  content: ReactNode;
  /** Position of the popup relative to the trigger */
  position?: PopupPosition;
  /** Alignment of the popup relative to the trigger */
  align?: PopupAlign;
  /** Whether to show a pointing arrow */
  pointing?: boolean;
  /** Controls open state (for controlled component) */
  open?: boolean;
  /** Default open state (for uncontrolled component) */
  defaultOpen?: boolean;
  /** Callback when open state changes */
  onOpenChange?: (open: boolean) => void;
  /** Whether to trap focus inside the popup */
  trapFocus?: boolean;
  /** Size variant affecting padding and max-width */
  size?: PopupSize;
  /** Offset from the trigger element in pixels */
  offset?: number;
  /** Whether the popup should automatically resize to fit content */
  autoSize?: boolean | 'width' | 'height';
  /** Renders the popup inline instead of in a portal */
  inline?: boolean;
  /** How the popup is triggered: 'click' (default), 'hover', or 'context' (right-click) */
  on?: 'click' | 'hover' | 'context';
  /** Delay in ms before closing the popup when mouse leaves (only for on="hover") */
  mouseLeaveDelay?: number;
  /** Whether the popup should close when scrolling */
  closeOnScroll?: boolean;
}

// Internal mapping: our position + align to Fluent positioning.
// Fluent aligns on a different axis depending on the position:
// - above/below accept the horizontal axis: start | center | end
// - before/after accept the vertical axis: top | center | bottom
// Our generic start/end must therefore be mapped to top/bottom for before/after,
// otherwise an invalid shorthand (e.g. "after-start") is produced. Fluent's
// resolvePositioningShorthand returns undefined for unknown shorthands, which
// crashes usePopover_unstable (reading 'coverTarget' of undefined).
const getFluentPositioning = (
  position: PopupPosition,
  align: PopupAlign,
  offset?: number,
  autoSize?: boolean | 'width' | 'height',
): PositioningShorthand => {
  const isVerticalAxis = position === 'before' || position === 'after';

  const fluentAlign =
    align === 'center' ? 'center' : isVerticalAxis ? (align === 'start' ? 'top' : 'bottom') : align;

  const alignSuffix = fluentAlign === 'center' ? '' : `-${fluentAlign}`;
  const positionValue = `${position}${alignSuffix}`;

  // If we need complex positioning options, return an object
  if (offset !== undefined || autoSize) {
    return {
      position,
      align: fluentAlign,
      ...(offset !== undefined && { offset: { mainAxis: offset } }),
      ...(autoSize && { autoSize: autoSize === true ? 'width-always' : `${autoSize}-always` }),
    };
  }

  return positionValue as PositioningShorthand;
};

/**
 * Popup component with semantic API.
 * Implementation uses Fluent UI Popover internally but exposes a library-agnostic interface.
 *
 * @example
 * // Basic usage
 * <Popup
 *   trigger={<Button>Click me</Button>}
 *   content={<div>Popup content</div>}
 * />
 *
 * @example
 * // With positioning and pointing arrow
 * <Popup
 *   trigger={<Button>Menu</Button>}
 *   content={<MenuContent />}
 *   position="below"
 *   align="start"
 *   pointing
 * />
 *
 * @example
 * // Controlled popup
 * <Popup
 *   trigger={<Button>Open</Button>}
 *   content={<div>Content</div>}
 *   open={isOpen}
 *   onOpenChange={setIsOpen}
 * />
 */
export function Popup({
  trigger,
  content,
  position = 'below',
  align = 'center',
  pointing = false,
  open,
  defaultOpen,
  onOpenChange,
  trapFocus = false,
  size = 'medium',
  offset,
  autoSize,
  inline = false,
  on = 'click',
  mouseLeaveDelay,
  closeOnScroll = false,
  className,
  ...rest
}: PopupProps) {
  const surfaceClass = cn(
    'aix-popup',
    {
      [`aix-popup--${size}`]: size !== 'medium',
      'aix-popup--pointing': pointing,
    },
    className,
  );

  const positioning = getFluentPositioning(position, align, offset, autoSize);

  const handleOpenChange = (_event: OpenPopoverEvents, data: OnOpenChangeData) => {
    onOpenChange?.(data.open);
  };

  // Determine if controlled or uncontrolled
  const isControlled = open !== undefined;

  // Get portal mount node inside FluentProvider for proper theme support.
  // When rendering inline, skip the portal entirely.
  const themedMountNode = useFluentMountNode();
  const mountNode = inline ? null : themedMountNode;

  return (
    <Popover
      positioning={positioning}
      trapFocus={trapFocus}
      open={isControlled ? open : undefined}
      defaultOpen={defaultOpen}
      onOpenChange={handleOpenChange}
      closeOnScroll={closeOnScroll}
      withArrow={pointing}
      inline={inline}
      mountNode={mountNode}
      openOnHover={on === 'hover'}
      openOnContext={on === 'context'}
      mouseLeaveDelay={mouseLeaveDelay}
    >
      <PopoverTrigger disableButtonEnhancement>{trigger}</PopoverTrigger>
      <PopoverSurface className={surfaceClass} {...rest}>
        {content}
      </PopoverSurface>
    </Popover>
  );
}

Popup.displayName = 'Popup';
